import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiDownload, FiUpload, FiFileText, FiImage } from 'react-icons/fi';

interface GovernmentScheme {
  id: number;
  title: string;
  title_hindi?: string;
  description: string;
  description_hindi?: string;
  category: string;
  image: string;
  eligibility: string;
  benefits: string[] | string;
  documents?: string;
  application_process?: string;
  link?: string;
  status?: string;
  created_at: string;
}

const GovernmentSchemes: React.FC = () => {
  const [schemes, setSchemes] = useState<GovernmentScheme[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Add Scheme Entry state
  const [addForm, setAddForm] = useState({
    title: '',
    title_hindi: '',
    description: '',
    description_hindi: '',
    category: '',
    eligibility: '',
    benefits: '',
    application_process: '',
    link: '',
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);

  // Helper function to construct proper image URL
  const getImageUrl = (imagePath: string) => {
    if (!imagePath) return '';
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) return imagePath;
    
    // Convert backslashes to forward slashes and construct full URL
    const cleanPath = imagePath.replace(/\\/g, '/').replace(/^\/+/, '');
    return `https://mandilive.in/${cleanPath}`;
  };

  const handleAddFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setAddForm(f => ({ ...f, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Add/Update Scheme Entry handler
  const handleSaveScheme = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    setAddSuccess('');
    setAddLoading(true);
    try {
      const url = 'https://mandilive.in/api/government-schemes';
      
      if (editingId) {
        // For updates, use JSON (no file upload on updates for now)
        const payload = {
          id: editingId,
          ...addForm,
          image: imagePreview || '', // Keep existing image if no new one
        };
        
        const res = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        
        if (res.ok) {
          setAddSuccess('Government scheme updated.');
          setAddError('');
          setEditingId(null);
          // Reset form
          setAddForm({
            title: '',
            title_hindi: '',
            description: '',
            description_hindi: '',
            category: '',
            eligibility: '',
            benefits: '',
            application_process: '',
            link: '',
          });
          setSelectedImage(null);
          setImagePreview('');
          // Refresh schemes
          fetchSchemes();
        } else {
          let err;
          try {
            err = await res.json();
          } catch (e) {
            err = { message: res.statusText };
          }
          console.error('API error:', res.status, err);
          setAddError(err.message || 'Failed to update government scheme');
        }
      } else {
        // For new entries, use FormData to handle file upload
        const formData = new FormData();
        formData.append('title', addForm.title);
        formData.append('title_hindi', addForm.title_hindi);
        formData.append('description', addForm.description);
        formData.append('description_hindi', addForm.description_hindi);
        formData.append('category', addForm.category);
        formData.append('eligibility', addForm.eligibility);
        formData.append('benefits', addForm.benefits);
        formData.append('application_process', addForm.application_process);
        formData.append('link', addForm.link);
        
        if (selectedImage) {
          formData.append('image', selectedImage);
        }
        
        const res = await fetch(url, {
          method: 'POST',
          body: formData, // No Content-Type header for FormData
        });
        
        if (res.ok) {
          setAddSuccess('Government scheme added.');
          setAddError('');
          // Reset form
          setAddForm({
            title: '',
            title_hindi: '',
            description: '',
            description_hindi: '',
            category: '',
            eligibility: '',
            benefits: '',
            application_process: '',
            link: '',
          });
          setSelectedImage(null);
          setImagePreview('');
          // Refresh schemes
          fetchSchemes();
        } else {
          let err;
          try {
            err = await res.json();
          } catch (e) {
            err = { message: res.statusText };
          }
          console.error('API error:', res.status, err);
          setAddError(err.message || 'Failed to save government scheme');
        }
      }
    } catch (err: any) {
      setAddError(err?.message || 'Failed to save government scheme');
    }
    setAddLoading(false);
  };

  // Fetch schemes data from API
  const fetchSchemes = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://mandilive.in/api/government-schemes');
      
      // Check if response is ok
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      // Get response text first to debug
      const responseText = await res.text();
      console.log('Raw response:', responseText);
      
      // Try to parse JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Invalid JSON response from server');
      }
      
      // Handle different response structures
      if (data && data.data && Array.isArray(data.data)) {
        setSchemes(data.data);
      } else if (Array.isArray(data)) {
        setSchemes(data);
      } else {
        console.error('Unexpected response structure:', data);
        setSchemes([]);
      }
    } catch (err: any) {
      console.error('Fetch error:', err);
      setError(err?.message || 'Failed to fetch government schemes');
    }
    setLoading(false);
  };

  // Delete scheme
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this government scheme?')) return;
    
    try {
      const res = await fetch('https://mandilive.in/api/government-schemes', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      
      if (res.ok) {
        setSchemes(schemes.filter(scheme => scheme.id !== id));
      } else {
        alert('Failed to delete government scheme');
      }
    } catch (err) {
      alert('Failed to delete government scheme');
    }
  };

  // Edit scheme
  const handleEdit = (scheme: GovernmentScheme) => {
    setEditingId(scheme.id);
    setAddForm({
      title: scheme.title,
      title_hindi: scheme.title_hindi || '',
      description: scheme.description,
      description_hindi: scheme.description_hindi || '',
      category: scheme.category,
      eligibility: scheme.eligibility,
      benefits: Array.isArray(scheme.benefits) ? scheme.benefits.join(', ') : scheme.benefits,
      application_process: scheme.documents || scheme.application_process || '',
      link: scheme.link || '',
    });
    setImagePreview(scheme.image);
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingId(null);
    setAddForm({
      title: '',
      title_hindi: '',
      description: '',
      description_hindi: '',
      category: '',
      eligibility: '',
      benefits: '',
      application_process: '',
      link: '',
    });
    setSelectedImage(null);
    setImagePreview('');
    setAddError('');
    setAddSuccess('');
  };

  useEffect(() => {
    fetchSchemes();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FiFileText className="mr-3 text-blue-600" />
            Government Schemes Management
          </h1>
          <p className="text-gray-600 mt-1">Manage government schemes and programs</p>
        </div>
        
      </div>

      {/* Add/Edit Scheme Entry Form */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <FiPlus className="mr-2" />
          {editingId ? 'Edit Government Scheme' : 'Add New Government Scheme'}
        </h2>
        <form onSubmit={handleSaveScheme} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                name="title"
                value={addForm.title}
                onChange={handleAddFormChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Scheme title"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title (Hindi)</label>
              <input
                type="text"
                name="title_hindi"
                value={addForm.title_hindi}
                onChange={handleAddFormChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Scheme title in Hindi"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                name="description"
                value={addForm.description}
                onChange={handleAddFormChange}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Scheme description"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (Hindi)</label>
              <textarea
                name="description_hindi"
                value={addForm.description_hindi}
                onChange={handleAddFormChange}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Scheme description in Hindi"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input
                type="text"
                name="category"
                value={addForm.category}
                onChange={handleAddFormChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Agriculture, Rural Development, Subsidy"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link</label>
              <input
                type="url"
                name="link"
                value={addForm.link}
                onChange={handleAddFormChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com/scheme-details"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Eligibility Criteria</label>
            <textarea
              name="eligibility"
              value={addForm.eligibility}
              onChange={handleAddFormChange}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Who can apply for this scheme"
            />
          </div>
            
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Benefits</label>
            <textarea
              name="benefits"
              value={addForm.benefits}
              onChange={handleAddFormChange}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="What benefits does this scheme provide"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Application Process</label>
            <textarea
              name="application_process"
              value={addForm.application_process}
              onChange={handleAddFormChange}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="How to apply for this scheme"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-md border"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={addLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {addLoading ? 'Saving...' : (
                <>
                  <FiPlus className="mr-2" />
                  {editingId ? 'Update Scheme' : 'Save Scheme'}
                </>
              )}
            </button>
            
            {editingId && (
              <button
                type="button"
                onClick={cancelEdit}
                className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Cancel Edit
              </button>
            )}
          </div>

          {addError && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {addError}
            </div>
          )}
          
          {addSuccess && (
            <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
              {addSuccess}
            </div>
          )}
        </form>
      </div>

      {/* Schemes List */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Government Schemes ({schemes.length})</h2>
        </div>
        
        {loading && (
          <div className="p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading government schemes...</p>
          </div>
        )}
        
        {error && (
          <div className="p-6">
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          </div>
        )}
        
        {!loading && !error && schemes.length === 0 && (
          <div className="p-6 text-center text-gray-500">
            <FiFileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p>No government schemes found. Add your first scheme above.</p>
          </div>
        )}
        
        {!loading && schemes.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scheme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Benefits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {schemes.map((scheme) => (
                  <tr key={scheme.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {scheme.image && (
                          <img
                            src={getImageUrl(scheme.image)}
                            alt={scheme.title}
                            className="h-10 w-10 rounded-full object-cover mr-3"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {scheme.title}
                          </div>
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {scheme.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {scheme.category || 'General'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                      {scheme.benefits}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(scheme.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {/* <button
                          onClick={() => handleEdit(scheme)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Edit"
                        >
                          <FiEdit />
                        </button> */}
                        <button
                          onClick={() => handleDelete(scheme.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GovernmentSchemes;
